import EditIcon from "@mui/icons-material/Edit";
import EditOffIcon from "@mui/icons-material/EditOff";
import SaveIcon from "@mui/icons-material/Save";
import { Box, CircularProgress, IconButton, InputLabel, Stack } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";

import { Option } from "components/common/Autocomplete/Autocomplete";
import { DetectorGroupAutocomplete } from "components/detectorManagement/FilterDetector/DetectorGroupAutocomplete/DetectorGroupAutocomplete";
import { DetectorGroupOption } from "components/detectorManagement/FilterDetector/DetectorGroupAutocomplete/types";
import { EditableDetectorFields } from "pages/detectorDetail/DetectorDetail";
import { DEFAULT_CONFIDENCE_THRESHOLD, Detector } from "types/detectors";

interface GroupSelectionProps {
  editableDetectorFields: EditableDetectorFields;
  setEditableDetectorFields: React.Dispatch<React.SetStateAction<EditableDetectorFields>>;
  detectorFetchData: Detector;
  updateDetector: (arg1: string, arg: any) => Promise<Detector>;
  getDetector: (route?: string | undefined) => Promise<Detector>;
}

interface CustomEditIconProps {
  isEditing: boolean;
  hasChanges: boolean;
  updating: boolean;
}

function CustomEditIcon(props: CustomEditIconProps) {
  const { isEditing, hasChanges, updating } = props;
  if (updating) {
    return <CircularProgress size={20} />;
  }
  if (hasChanges) {
    return <SaveIcon />;
  }
  return isEditing ? <EditOffIcon /> : <EditIcon />;
}

function GroupSelection(props: GroupSelectionProps) {
  const { editableDetectorFields, setEditableDetectorFields, detectorFetchData, updateDetector, getDetector } = props;
  const hasChanges = detectorFetchData.set.id !== editableDetectorFields.set.id;
  const [groupDataUpdating, setGroupDataLoading] = useState(false);
  const [isEditingGroup, setIsEditingGroup] = useState<boolean>(false);

  /**
   * When the detector group is updated the groupDataUpdating is set to true,
   * with this effect once the value is updated set groupDataUpdating will be
   * set back to false.
   */
  useEffect(() => {
    if (!hasChanges) {
      setGroupDataLoading(false);
    }
  }, [hasChanges]);

  // autocomplete / typeahead
  const updateGroupSet = (option?: DetectorGroupOption) => {
    if (option) {
      setEditableDetectorFields({
        ...editableDetectorFields,
        set: {
          name: option.label,
          id: option.value,
        },
      });
    }
  };

  useEffect(() => {
    setEditableDetectorFields({
      confidence_threshold: detectorFetchData?.confidence_threshold || DEFAULT_CONFIDENCE_THRESHOLD,
      set: detectorFetchData?.set,
    });
  }, [detectorFetchData?.confidence_threshold, detectorFetchData?.set, setEditableDetectorFields]);

  const onEditButtonClick = () => {
    if (hasChanges && isEditingGroup) {
      const patchPromise = updateDetector("", editableDetectorFields);
      setGroupDataLoading(true);
      patchPromise.then(() => {
        setIsEditingGroup(false);
        getDetector();
      });
    } else {
      setIsEditingGroup(!isEditingGroup);
    }
  };

  const value: Option<string> = useMemo(
    () => ({
      label: editableDetectorFields.set.name,
      value: editableDetectorFields.set.id,
    }),
    [editableDetectorFields.set.name, editableDetectorFields.set.id]
  );

  return (
    <Box>
      <InputLabel className="uppercase text-sm text-neutral-700 font-bold mb-2">Group:</InputLabel>
      <Stack direction="row">
        <Box className="w-full">
          <DetectorGroupAutocomplete
            label="Choose a detector group set"
            disabled={!isEditingGroup || groupDataUpdating}
            autoHighlight
            value={value}
            onChange={updateGroupSet}
            inputProps={{
              autoComplete: "new-password",
            }}
          />
        </Box>

        <div>
          <IconButton aria-label="edit button" onClick={onEditButtonClick}>
            <CustomEditIcon hasChanges={hasChanges} updating={groupDataUpdating} isEditing={isEditingGroup} />
          </IconButton>
        </div>
      </Stack>
    </Box>
  );
}

export default GroupSelection;
